import PropTypes from 'prop-types'
import { getInlineComponent, INLINE_COMPONENT_TYPES } from './inline'

const StandaloneComponentRenderer = ({
  component,
  components,
  layout = 'vertical',
  onComponentAction,
}) => {
  const renderComponent = (componentData, index = 0) => {
    const { type, text, content, action, href, ...otherProps } = componentData
    const Component = getInlineComponent(type)

    if (!Component) {
      console.warn(`Unknown component type: ${type}`)
      return null
    }

    // Create stable key from component properties and index
    const stableKey = `${type}-${index}-${text || content || action || href || 'empty'}`

    return (
      <Component
        key={stableKey}
        text={text}
        content={content}
        action={action}
        href={href}
        {...otherProps}
        onAction={onComponentAction}
      />
    )
  }

  // Single component case
  if (component) {
    return <div className="standalone-components">{renderComponent(component, 0)}</div>
  }

  // Multiple components case
  if (components && Array.isArray(components)) {
    const containerClass =
      layout === 'horizontal' ? 'flex flex-wrap gap-2 items-center' : 'flex flex-col gap-2'

    return (
      <div className={`standalone-components ${containerClass}`}>
        {components.map((comp, index) => (
          <div key={`comp-wrapper-${index}`}>{renderComponent(comp, index)}</div>
        ))}
      </div>
    )
  }

  return null
}

// Define the shape for component props based on all inline component types
const componentShape = PropTypes.shape({
  type: PropTypes.oneOf(INLINE_COMPONENT_TYPES).isRequired,
  // Common props
  text: PropTypes.string,
  content: PropTypes.string,
  // Button specific props
  action: PropTypes.string,
  variant: PropTypes.oneOf(['primary', 'secondary', 'success', 'danger', 'outline']),
  disabled: PropTypes.bool,
  // Link specific props
  href: PropTypes.string,
  external: PropTypes.bool,
  // Badge specific props
  color: PropTypes.oneOf(['green', 'red', 'blue', 'yellow', 'gray', 'purple', 'cyan']),
  // Code specific props
  copyable: PropTypes.bool,
  // Heading specific props
  level: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
})

StandaloneComponentRenderer.propTypes = {
  component: componentShape,
  components: PropTypes.arrayOf(componentShape),
  layout: PropTypes.oneOf(['horizontal', 'vertical']),
  onComponentAction: PropTypes.func.isRequired,
}

export default StandaloneComponentRenderer
